/**
 * Utility functions for workspace member management
 */

import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { User } from "firebase/auth";

/**
 * Ensures a user document exists in the users collection
 * Creates it if it doesn't exist, using data from Firebase Auth user
 * 
 * @param user - Firebase Auth user object
 * @returns Promise that resolves when user document is guaranteed to exist
 */
export async function ensureUserDocumentExists(user: User): Promise<void> {
  const userRef = doc(db, "users", user.uid);
  const userDoc = await getDoc(userRef);
  
  if (!userDoc.exists()) {
    // Create user document if it doesn't exist
    // Note: termsAccepted is NOT set here - it will be set when user accepts terms
    // during first login/registration (handled by App.tsx terms dialog)
    await setDoc(userRef, {
      uid: user.uid,
      email: user.email || null,
      name: user.displayName || null,
      displayName: user.displayName || null,
      photoURL: user.photoURL || null,
      activeWorkspaceId: null,
      createdAt: serverTimestamp(),
    });
  } else {
    // Update user document if it exists but is missing critical fields
    const userData = userDoc.data();
    const needsUpdate = !userData?.name && !userData?.email && !userData?.displayName;
    
    if (needsUpdate && (user.displayName || user.email)) {
      await setDoc(userRef, {
        ...userData,
        email: userData.email || user.email || null,
        name: userData.name || user.displayName || null,
        displayName: userData.displayName || user.displayName || null,
        photoURL: userData.photoURL || user.photoURL || null,
      }, { merge: true });
    }
  }
}

/**
 * Validates that a user document exists and has meaningful data
 * before creating a member document
 * 
 * @param userId - User ID to validate
 * @returns Promise that resolves to true if user document exists and has data, false otherwise
 */
export async function validateUserDocumentExists(userId: string): Promise<boolean> {
  try {
    const userDoc = await getDoc(doc(db, "users", userId));
    
    if (!userDoc.exists()) {
      return false;
    }
    
    const userData = userDoc.data();
    // Check if user document has meaningful data
    return !!(userData?.name || userData?.email || userData?.displayName);
  } catch (error) {
    console.error(`Error validating user document for ${userId}:`, error);
    return false;
  }
}
